import { Alert, Button, message, Modal, Spin, Upload } from 'antd'
import React, { useState } from 'react'
import BAPLIE_FILE_TYPES from 'constants/baplieFileTypes'
import PropTypes from 'prop-types'
import { UploadOutlined } from '@ant-design/icons'

import './baplieOrRestowUploadModal.less'

const BaplieOrRestowUploadModal = ({
  fileType,
  isOpen,
  isUploading,
  onClose,
  onUpload,
  t,
  uploadError,
}) => {
  const [fileList, setFileList] = useState([])
  const [isProcessing, setIsProcessing] = useState(false)
  // Show loading overlay from submit → POST → background refetches → onSuccess/onError
  const isOverlaying = isProcessing || isUploading

  if (!fileType) {
    return null
  }

  const getAcceptedFileType = () => {
    if (fileType === BAPLIE_FILE_TYPES.RESTOW) {
      return '.txt'
    }

    return '.edi'
  }

  const readFileAsBase64 = file =>
    new Promise((resolve, reject) => {
      const reader = new FileReader()

      reader.onload = () => {
        const result = reader.result
        if (typeof result === 'string') {
          const base64Content = result.split(',')[1] || ''
          resolve(base64Content)
        } else {
          resolve('')
        }
      }

      reader.onerror = reject
      reader.readAsDataURL(file)
    })

  const validateFile = file => {
    const fileName = file.name.toLowerCase()
    const expectedExtension = getAcceptedFileType()

    if (!fileName.endsWith(expectedExtension)) {
      const fileTypeLabel =
        fileType === BAPLIE_FILE_TYPES.RESTOW
          ? t('portCall.shipOverview.baplieOrRestowInfo.restow')
          : t('portCall.shipOverview.baplieOrRestowInfo.baplie')

      message.error(
        `${t(
          'portCall.shipOverview.baplieOrRestowInfo.pleaseUpload'
        )} ${expectedExtension} ${t(
          'portCall.shipOverview.baplieOrRestowInfo.fileFor'
        )} ${fileTypeLabel}`
      )

      return false
    }

    return true
  }

  const handleUploadChange = info => {
    const { file } = info

    if (file.status !== 'removed' && !validateFile(file)) {
      return
    }

    let newFileList = [...info.fileList]

    newFileList = newFileList.slice(-1)

    setFileList(newFileList)
  }

  const uploadProps = {
    accept: getAcceptedFileType(),
    beforeUpload: file => {
      if (!validateFile(file)) {
        return Upload.LIST_IGNORE
      }

      return false
    },
    fileList,
    multiple: false,
    name: 'file',
    onChange: handleUploadChange,
    onDrop: e => {
      const files = e.dataTransfer.files
      if (files.length > 0) {
        const file = files[0]
        validateFile(file)
      }
    },
  }

  const handleUploadSubmit = async () => {
    if (fileList.length === 0) {
      message.warning(
        t('portCall.shipOverview.baplieOrRestowInfo.pleaseSelectFile')
      )

      return
    }

    const selectedFile = fileList[0]?.originFileObj

    if (!selectedFile) {
      message.error(
        t('portCall.shipOverview.baplieOrRestowInfo.fileUploadFailed')
      )

      return
    }

    try {
      const base64Content = await readFileAsBase64(selectedFile)

      if (!base64Content) {
        message.error(
          t('portCall.shipOverview.baplieOrRestowInfo.fileUploadFailed')
        )

        return
      }

      if (onUpload) {
        setIsProcessing(true)
        onUpload({
          file: base64Content,
          filename: selectedFile.name,
          onError: () => {
            setIsProcessing(false)
          },
          onSuccess: () => {
            setIsProcessing(false)
            message.success(
              t('portCall.shipOverview.baplieOrRestowInfo.fileUploadSuccessful')
            )
            setFileList([])
            onClose()
          },
          type: fileType,
        })
      }
    } catch (error) {
      message.error(
        t('portCall.shipOverview.baplieOrRestowInfo.fileUploadFailed')
      )
    }
  }

  const handleClose = () => {
    setFileList([])
    onClose()
  }

  const getModalTitle = () => {
    switch (fileType) {
      case BAPLIE_FILE_TYPES.LOAD:
        return t('portCall.shipOverview.baplieOrRestowInfo.uploadLoadBaplie')
      case BAPLIE_FILE_TYPES.DISCHARGE:
        return t(
          'portCall.shipOverview.baplieOrRestowInfo.uploadDischargeBaplie'
        )
      case BAPLIE_FILE_TYPES.RESTOW:
      default:
        return t('portCall.shipOverview.baplieOrRestowInfo.uploadRestow')
    }
  }

  return (
    <>
      <Modal
        closable={!isOverlaying}
        footer={null}
        maskClosable={!isOverlaying}
        onCancel={isOverlaying ? undefined : handleClose}
        open={isOpen}
        title={getModalTitle()}
      >
        <div className="baplie-or-restow-upload-modal__content">
          {isOverlaying && (
            <div className="baplie-or-restow-upload-modal__overlay">
              <Spin size="large" />
            </div>
          )}

          <Upload.Dragger
            {...uploadProps}
            className="baplie-or-restow-drawer__upload"
          >
            <p className="ant-upload-drag-icon">
              <UploadOutlined />
            </p>
            <p className="ant-upload-text">
              {t('portCall.shipOverview.baplieOrRestowInfo.uploadDragDropText')}
            </p>
          </Upload.Dragger>

          <Button
            className="baplie-or-restow-drawer__upload-button"
            disabled={fileList.length === 0 || isOverlaying}
            onClick={handleUploadSubmit}
            type="primary"
          >
            {t('portCall.shipOverview.baplieOrRestowInfo.upload')}
          </Button>

          {uploadError && !isOverlaying && (
            <div className="baplie-or-restow-upload-modal__error">
              <Alert banner message={uploadError} showIcon type="error" />
            </div>
          )}
        </div>
      </Modal>
    </>
  )
}

BaplieOrRestowUploadModal.propTypes = {
  fileType: PropTypes.oneOf(Object.values(BAPLIE_FILE_TYPES)),
  isOpen: PropTypes.bool.isRequired,
  isUploading: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onUpload: PropTypes.func,
  t: PropTypes.func.isRequired,
  uploadError: PropTypes.string,
}

export default BaplieOrRestowUploadModal
